'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CollectionCardsProps } from '@/components/collection-cards/types';
import { fetchItemsByCollectionPage } from '@/api/content-hub';
import { fetchAllCategories } from '@/api/category';
import { CardProps } from '@/components/card/types';
import Card from '@/components/card/Card';
import { Headline } from '@/components/headline/Headline';
import ChipsWrapper from '@/components/chips/ChipsWrapper';
import clsx from 'clsx';
import { Button } from '@/components/button/Button';
import { Link } from '@/components/link/Link';

export const CollectionCards: React.FC<CollectionCardsProps> = ({
  collection,
  limit = 6,
  categories: sectionCategories = [],
  locale,
  headline,
  spacingTop,
  spacingBottom,
  sectionBackgroundColor,
  hideCategories,
  sectionId,
  link,
}) => {
  const pageLimit = Math.max(1, limit);
  const sectionCategoryTitles = useMemo(
    () =>
      sectionCategories.map((category: any) => category?.title).filter(Boolean),
    [sectionCategories],
  );
  const [cards, setCards] = useState<CardProps[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [offset, setOffset] = useState(0); // Offset for pagination
  const [hasMore, setHasMore] = useState(false); // Flag to track if more data exists
  const [loading, setLoading] = useState(false);

  const loadCards = useCallback(
    async (newOffset: number, selectedCategories: string[] = []) => {
      setLoading(true);
      try {
        const categoryFilters = selectedCategories.length
          ? selectedCategories
          : sectionCategoryTitles;
        const { items: fetchedCards, hasMore } =
          await fetchItemsByCollectionPage(
            collection,
            locale,
            pageLimit,
            categoryFilters,
            newOffset,
          );

        setCards((prevCards) =>
          newOffset === 0 ? fetchedCards : [...prevCards, ...fetchedCards],
        );

        setHasMore(hasMore);
      } catch (error) {
        console.error('Error loading cards:', error);
      }
      setLoading(false);
    },
    [collection, locale, pageLimit, sectionCategoryTitles],
  );

  const loadCategories = useCallback(async () => {
    if (sectionCategoryTitles.length) {
      setCategories(sectionCategoryTitles);
      return;
    }

    try {
      const fetchedCategories = await fetchAllCategories(locale);
      setCategories(fetchedCategories.map((category: any) => category.title));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [locale, sectionCategoryTitles]);

  useEffect(() => {
    loadCards(0, []);
    loadCategories();
  }, [loadCards, loadCategories]);

  const handleCategorySelectionChange = (selected: string[]) => {
    setSelectedCategories(selected);
    setOffset(0);
    setHasMore(true);
    loadCards(0, selected);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (loading) {
      return;
    }

    const nextOffset = offset + pageLimit;
    setOffset(nextOffset);
    loadCards(nextOffset, selectedCategories);
  };

  return (
    <div
      id={sectionId}
      className={clsx(
        'w-full py-12',
        spacingTop ? 'lg:pt-20' : '',
        spacingBottom ? 'lg:pb-20' : '',
        sectionBackgroundColor ? `bg-${sectionBackgroundColor}` : '',
      )}
    >
      <div className="container mx-auto flex w-full flex-col gap-10 px-4 sm:px-0">
        {headline && <Headline {...headline} />}
        <div className={clsx('flex flex-col gap-6')}>
          {!hideCategories && categories.length > 0 && (
            <ChipsWrapper
              options={categories}
              selectedOptions={selectedCategories}
              onSelectionChange={handleCategorySelectionChange}
              variant="inline-wrap"
              selectionMode="single"
            />
          )}
        </div>
        <div className="grid gap-4 overflow-hidden sm:grid-cols-4 lg:grid-cols-12">
          {cards.map((cardProps: CardProps, index: number) => (
            <div key={`card-${index + 1}`} className="col-span-4">
              <Card {...cardProps} />
            </div>
          ))}
        </div>
        {loading && (
          <span className="flex justify-center rounded px-4 py-2 text-brand-700">
            <svg
              className="mr-3 h-5 w-5 animate-spin text-brand-700"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C3.58 0 0 5.82 0 12h4z"
              ></path>
            </svg>
            Loading...
          </span>
        )}
        {!link && !loading && hasMore && (
          <div className="flex justify-center">
            <Button
              aria-label={'load more'}
              onClick={handleLoadMore}
              rightIcon={{ name: 'refresh-cw' }}
              name="Load more"
            />
          </div>
        )}

        {link && (
          <div className="flex w-full justify-center">
            <Link {...link} />
          </div>
        )}
      </div>
    </div>
  );
};
